"use client";

/**
 * Sources — the report's numbered bibliography, and the destination every
 * citation marker in the document jumps to.
 *
 * Extracted from the design source `compass-oppreport.jsx` (the `bibliography`
 * section, `BIB_REPORT` and the `.rv-bib*` rules): `.rv-intro` over
 * `.rv-biblist`, one `.rv-bib` per entry carrying its `.rv-bibn` ordinal beside
 * a `.rv-bibmain` column of title, meta, context, trust note and either an
 * external link or a system-of-record label.
 *
 * Two things this section deliberately is not:
 *
 * - **Not the per-value source list.** `SourceList` / `CiteDetail` are the
 *   intake chat rail's, and the formula trace modal already shows the sources
 *   behind an individual model value. The intro copy points readers there on
 *   purpose — a report-level bibliography and per-value provenance are
 *   different things.
 * - **Not a durable numbering.** `ReportSource.n` is assigned by the mapper at
 *   render time from the identity a claim stores, because nothing in the stack
 *   records a citation ordinal (see `ReportSource`). This component only ever
 *   renders the number it is handed.
 *
 * The design's `BacksModal` drill-in is dead code there — no `BIB_REPORT` entry
 * carries `backs` — so the "Backs N values" control renders only when the count
 * is real *and* a caller has somewhere to send the click.
 */

import { useEffect, useState } from "react";
import { ArrowUpR } from "../lib/icons";
import type { ReportSource } from "./report-types";

/** The design's `goBib` highlight window (`compass-oppreport.jsx`). */
const FLASH_DURATION_MS = 1200;

/**
 * A citation marker elsewhere in the report jumped to an entry.
 *
 * Carried as an object rather than a bare number so that clicking the *same*
 * marker twice re-arms the highlight: the effect below keys off the object's
 * identity, and the caller hands over a fresh one per click. The flash itself
 * is React state on this component rather than a class mutated onto the DOM
 * node, so it survives a re-render of the report document mid-animation.
 */
export interface ReportSourceFlashTarget {
  n: number;
}

export interface ReportSourcesProps {
  sources: ReportSource[];
  /** Omit ⇒ no "Backs N values" control, however real the count is. */
  onOpenBacking?: (dedupKey: string) => void;
  /** The entry to flash, or `null`. */
  flashTarget?: ReportSourceFlashTarget | null;
}

/** `.rv-intro` — verbatim from the design. */
const INTRO_COPY =
  "Market research, industry benchmarks, and internal strategy behind the claims in this report. Numbered markers in the text above link here. Provenance for individual model values lives in the assumptions table.";

/** `.rv-bib` — the row, its hairline rule, and the flash transition. */
const BIB_ENTRY = [
  "flex gap-4 py-4",
  "border-t border-(--border-subtle) first:border-t-0",
  "transition-colors duration-300 ease-(--ease)",
].join(" ");

/** `.rv-bibn` — the mono ordinal in its fixed 34px gutter. */
const BIB_NUMBER = [
  "shrink-0 basis-[34px]",
  "font-[family-name:var(--font-mono)] text-[22px] font-bold leading-none",
  "text-(--fg-subtle)",
].join(" ");

/** `.rv-bibtitle` — wraps so a long title and the backs control can share a line. */
const BIB_TITLE =
  "flex flex-wrap items-baseline gap-2.5 text-[14px] font-semibold text-(--fg-primary)";

/** `.rv-bibbacks` */
const BIB_BACKS = [
  "cursor-pointer border-0 bg-transparent p-0",
  "font-[family-name:var(--font-body)] text-[12px] font-medium",
  "text-(--fg-brand) underline underline-offset-2",
].join(" ");

/** `.rv-biblink` / `.rv-bibint` share a layout and differ only in colour. */
const BIB_FOOT = "mt-2 inline-flex items-center gap-[5px] text-[11.5px]";

/** The design's link label: the URL without its scheme or leading `www.`. */
function linkLabel(url: string): string {
  return url.replace(/^https?:\/\/(www\.)?/, "");
}

function backsLabel(count: number): string {
  return `Backs ${count} value${count === 1 ? "" : "s"}`;
}

/**
 * The bibliography. Numbering, dedupe and ordering all belong to whoever built
 * `sources` — this renders the array in the order it is handed, so a story and
 * the app cannot disagree about what entry 3 is.
 */
export function ReportSources({
  sources,
  onOpenBacking,
  flashTarget,
}: ReportSourcesProps) {
  const [flashedSourceN, setFlashedSourceN] = useState<number | null>(null);

  useEffect(() => {
    if (!flashTarget) return;
    setFlashedSourceN(flashTarget.n);
    const timer = window.setTimeout(
      () => setFlashedSourceN(null),
      FLASH_DURATION_MS,
    );
    return () => window.clearTimeout(timer);
  }, [flashTarget]);

  if (sources.length === 0) {
    // The intro would promise markers that link here, so it goes too.
    return (
      <p className="m-0 text-[12.5px] leading-[1.55] text-(--fg-muted)">
        This model does not cite any sources yet, so there is no bibliography to
        show. Sources appear here once the model's values carry evidence.
      </p>
    );
  }

  return (
    <>
      {/* `.rv-intro` */}
      <p className="mt-0 mb-3 text-[14px] leading-[1.6] text-(--fg-secondary)">
        {INTRO_COPY}
      </p>
      {/* `.rv-biblist` */}
      <ol className="m-0 flex list-none flex-col gap-0.5 p-0">
        {sources.map((source) => (
          <li
            key={source.dedupKey}
            // The anchor a citation marker scrolls to.
            id={`bib-${source.n}`}
            className={[
              BIB_ENTRY,
              flashedSourceN === source.n ? "bg-(--bg-sky)" : "",
            ].join(" ")}
          >
            <div className={BIB_NUMBER}>{source.n}</div>
            {/* `.rv-bibmain` */}
            <div className="min-w-0 flex-1">
              <div className={BIB_TITLE}>
                {source.title}
                {source.backsCount > 0 && onOpenBacking && (
                  <button
                    type="button"
                    className={BIB_BACKS}
                    onClick={() => onOpenBacking(source.dedupKey)}
                  >
                    {backsLabel(source.backsCount)}
                  </button>
                )}
              </div>
              {source.meta && (
                // `.rv-bibmeta`
                <div className="mt-[3px] text-[11.5px] text-(--fg-muted)">
                  {source.meta}
                </div>
              )}
              {source.context && (
                // `.rv-bibctx`
                <div className="mt-2 text-[12.5px] leading-[1.55] text-(--fg-secondary)">
                  {source.context}
                </div>
              )}
              {source.trust && (
                // `.rv-bibtrust`
                <div className="mt-1.5 text-[12px] leading-[1.5] text-(--fg-muted) italic">
                  {source.trust}
                </div>
              )}
              {source.urls.length > 0 ? (
                // `.rv-biblink`, once per backing document. The design shows a
                // single link because its fixture has one URL; an entry cited
                // with several must list them all or the bibliography
                // understates what the claim rests on.
                <div className="flex flex-col items-start">
                  {source.urls.map((url) => (
                    <a
                      key={url}
                      className={`${BIB_FOOT} text-(--fg-brand) no-underline hover:underline hover:underline-offset-2`}
                      href={url}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {linkLabel(url)}
                      <ArrowUpR size={11} />
                    </a>
                  ))}
                </div>
              ) : (
                source.internalLabel && (
                  // `.rv-bibint` — an internal source has nothing to link to.
                  <span className={`${BIB_FOOT} text-(--fg-muted)`}>
                    System of record: {source.internalLabel}
                  </span>
                )
              )}
            </div>
          </li>
        ))}
      </ol>
    </>
  );
}
